import type { PoolPlacement, PoolClimate, PoolUnit } from '@/api/calculators/types';
import { CLIMATE_OPTIONS, PLACEMENT_OPTIONS, POOL_MODEL_OPTIONS } from './constants';
import { SELECT_CLASS, INPUT_CLASS, Field } from './PoolUi';

interface ReverseFlowProps {
  model: string;
  setModel: (v: string) => void;
  climate: PoolClimate;
  setClimate: (v: PoolClimate) => void;
  placement: PoolPlacement;
  setPlacement: (v: PoolPlacement) => void;
  winterOp: 'yes' | 'no';
  setWinterOp: (v: 'yes' | 'no') => void;
  qty: number;
  setQty: (v: number) => void;
  length: number | '';
  setLength: (v: number | '') => void;
  width: number | '';
  setWidth: (v: number | '') => void;
  depthAvg: number | '';
  setDepthAvg: (v: number | '') => void;
  unit: PoolUnit;
  setUnit: (v: PoolUnit) => void;
  loading: boolean;
  onCalculate: () => void;
}

export function ReverseFlow(p: ReverseFlowProps) {
  return (
    <div className="rounded-lg bg-white p-5 shadow-sm">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Pool heat pump model">
          <select
            data-testid="pool-rev-model"
            value={p.model}
            onChange={(e) => p.setModel(e.target.value)}
            className={SELECT_CLASS}
          >
            {POOL_MODEL_OPTIONS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Qty">
          <select
            data-testid="pool-rev-qty"
            value={p.qty}
            onChange={(e) => p.setQty(Number(e.target.value))}
            className={SELECT_CLASS}
          >
            {[1, 2, 3].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Climate zone">
          <select
            data-testid="pool-rev-climate"
            value={p.climate}
            onChange={(e) => p.setClimate(e.target.value as PoolClimate)}
            className={SELECT_CLASS}
          >
            {CLIMATE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Placement">
          <select
            data-testid="pool-rev-placement"
            value={p.placement}
            onChange={(e) => p.setPlacement(e.target.value as PoolPlacement)}
            className={SELECT_CLASS}
          >
            {PLACEMENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Winter ops">
          <select
            data-testid="pool-rev-winter"
            value={p.winterOp}
            onChange={(e) => p.setWinterOp(e.target.value as 'yes' | 'no')}
            className={SELECT_CLASS}
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </Field>
      </div>

      <div className="mt-4">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
          Optional · fit check
        </div>
        <div className="grid grid-cols-4 gap-2">
          <Field label="Length">
            <input
              type="number"
              step={0.1}
              data-testid="pool-rev-length"
              value={p.length}
              onChange={(e) =>
                p.setLength(e.target.value === '' ? '' : Number(e.target.value))
              }
              className={INPUT_CLASS}
            />
          </Field>
          <Field label="Width">
            <input
              type="number"
              step={0.1}
              data-testid="pool-rev-width"
              value={p.width}
              onChange={(e) =>
                p.setWidth(e.target.value === '' ? '' : Number(e.target.value))
              }
              className={INPUT_CLASS}
            />
          </Field>
          <Field label="Depth">
            <input
              type="number"
              step={0.1}
              data-testid="pool-rev-depth"
              value={p.depthAvg}
              onChange={(e) =>
                p.setDepthAvg(e.target.value === '' ? '' : Number(e.target.value))
              }
              className={INPUT_CLASS}
            />
          </Field>
          <Field label="Unit">
            <select
              data-testid="pool-rev-unit"
              value={p.unit}
              onChange={(e) => p.setUnit(e.target.value as PoolUnit)}
              className={SELECT_CLASS}
            >
              <option value="m">m</option>
              <option value="ft">ft</option>
            </select>
          </Field>
        </div>
      </div>

      <button
        type="button"
        onClick={p.onCalculate}
        disabled={p.loading}
        className="mt-4 w-full rounded-md bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
        data-testid="pool-rev-calculate"
      >
        {p.loading ? 'Loading…' : 'Show capability envelope'}
      </button>
    </div>
  );
}

// ─── Reverse result view ────────────────────────────────────────────────────
